import React, { useEffect, Suspense } from 'react';
import { useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';
import { rtlEnable, localeSelector, hasUserRequireDataSelector } from '@/domain/env';
import DraggableToastContainer from '@/components/DraggableToastContainer';
import moment from 'moment';

import Fullstory from '@/components/Fullstory';
import OneSignalInit from '@/components/OneSignalInit';
import { AppStatusBar, ConnectionStatus, DiskSpaceStatus } from '@/components/AppStatusBar';
import CssBaseline from '@mui/material/CssBaseline';
import LinearProgress from '@mui/material/LinearProgress';
import MetadataManager from '@/components/MetadataManager';
import DialogPermissionsChanged from '@/pages/common/Dialog/DialogPermissionsChanged';

import 'react-toastify/dist/ReactToastify.css';
import '@/lib/toast.css';

const App: React.FC = () => {
  const isRtl = useSelector(rtlEnable);
  const locale = useSelector(localeSelector);
  const hasUserRequireData = useSelector(hasUserRequireDataSelector);
  const direction = isRtl ? 'rtl' : 'ltr';

  useEffect(() => {
    if (document.body) {
      document.body.setAttribute('class', direction);
      document.body.setAttribute('dir', direction);
    }

    moment.locale(locale);
  }, [locale, direction]);

  return (
    hasUserRequireData && (
      <>
        <CssBaseline />
        <Suspense fallback={<LinearProgress sx={{ width: '100%' }} />}>
          <Outlet />
        </Suspense>
        <DraggableToastContainer
          autoClose={3000}
          rtl={direction === 'rtl'}
          newestOnTop
          pauseOnHover
          hideProgressBar
          position={direction === 'rtl' ? 'top-left' : 'top-right'}
        />
        <DialogPermissionsChanged />
        <AppStatusBar />
        <ConnectionStatus />
        <DiskSpaceStatus />
        <Fullstory />
        <OneSignalInit />
        <MetadataManager />
      </>
    )
  );
};

export default App;
